// V1 source: customer/midas/components/MidasArtifactImageViewer.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 image viewer — Tier A behavioral parity with V1. Logic copied verbatim:
//   - URL helpers (isRelativePath, isBlobUrl, buildFetchUrl) — including
//     cross_reference diagram-image proxy + bot-image proxy + machineid
//     direct paths.
//   - Fetch with Authorization header → blob → setBlobUrl + setMimeType.
//   - Blob URL lifecycle (revokeObjectURL on unmount + on tab swap).
//   - ZOOM_STEPS [50, 75, 100, 150, 200] with handleZoomIn / handleZoomOut /
//     handleFit.
//   - Pan state (panX, panY) with mouse drag + ArrowLeft/Right/Up/Down
//     keyboard pan when canPan (zoom > 100).
//   - Keyboard shortcuts: + / = zoom-in, - zoom-out, arrows pan.
//   - Reset pan when zoom drops to ≤ 100.
//   - handleDownload — title + extension from MIME type.
//
// Visual layer rewritten in Tailwind. Icons from `lucide-react` (replaces
// MUI icons). Per Paper artboard `MIDAS V2 — Phase 2 — Dock — Image Viewer`:
// shimmer skeleton fills the dock body during fetch; "Content not loaded"
// fallback on fetch failure; bottom toolbar consolidated with mono % readout
// and 28×28 hit targets in 36–40px chrome strip.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minus, Plus, Download } from "lucide-react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import accountContext from "../../../../account/accountContext";
import { setArtifactZoom } from "../../midasPageSlice";
import { ArtifactTab } from "../../midasPageModels";

// ===================================================================
// Constants — verbatim from V1
// ===================================================================

const ZOOM_STEPS = [50, 75, 100, 150, 200];
const PAN_STEP = 40;

// ===================================================================
// URL Helpers — verbatim from V1
// ===================================================================

/** Validate that a URL is a relative path (SEC-REQ-5). */
const isRelativePath = (url: string): boolean => url.startsWith("/");

/** Check if a URL is a local blob URL (user-attached images). */
const isBlobUrl = (url: string): boolean => url.startsWith("blob:");

/** Build fetch URL based on artifact source. */
const buildFetchUrl = (tab: ArtifactTab): string | null => {
  const { artifact } = tab;

  // Blob URLs are already local — return directly (M7-A: user image upload)
  if (isBlobUrl(artifact.url)) return artifact.url;

  if (!isRelativePath(artifact.url)) return null;

  const baseUrl = (window as any).app?.config?.apiBaseUrl || "";

  // MC-25: cross_reference diagram overlays come from the bot's diagram API
  // (/api/v1/diagram/{page}/image), which has its own dedicated proxy route
  // (/v1/bot/diagram/{page}/image) — NOT the generic /v1/bot/image/ proxy.
  if (artifact.source === "cross_reference") {
    const diagramMatch = artifact.url.match(/\/api\/v1\/diagram\/(\d+)\/image/);
    if (diagramMatch) {
      return `${baseUrl}/v1/bot/diagram/${diagramMatch[1]}/image`;
    }
    // Unknown cross_reference URL shape — fall through to bot image proxy as best-effort
  }

  // Bot-hosted and user-uploaded (MIDAS-BUG-001-P2) images flow through the
  // generic bot gateway image proxy.
  if (
    artifact.source === "bot" ||
    artifact.source === "user" ||
    artifact.source === "cross_reference"
  ) {
    // Bot-hosted: use bot image proxy
    // Extract path after /api/v1/image/ — works for both relative and full URLs
    const marker = "/api/v1/image/";
    const markerIdx = artifact.url.indexOf(marker);
    const stripped =
      markerIdx !== -1
        ? artifact.url.slice(markerIdx + marker.length)
        : artifact.url.replace(/^\/+/, ""); // fallback: strip leading slashes
    return `${baseUrl}/v1/bot/image/${stripped}`;
  }

  // MachineID-hosted: direct API call
  return `${baseUrl}${artifact.url}`;
};

// ===================================================================
// MidasArtifactImageViewerV2
// ===================================================================

interface Props {
  tab: ArtifactTab;
}

const MidasArtifactImageViewerV2: React.FC<Props> = ({ tab }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mimeType, setMimeType] = useState<string>("image/jpeg");

  // Pan state
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const zoom = tab.zoom;
  const canPan = zoom > 100;

  // ── Fetch image ────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    setPanX(0);
    setPanY(0);

    const fetchUrl = buildFetchUrl(tab);
    if (!fetchUrl) {
      setError(true);
      setLoading(false);
      return;
    }

    // Blob URLs (user-attached images) can be used directly — no fetch needed
    if (isBlobUrl(fetchUrl)) {
      setBlobUrl(fetchUrl);
      setMimeType(tab.artifact.mime_type || "image/jpeg");
      setLoading(false);
      return;
    }

    let revoked = false;
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    fetch(fetchUrl, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        setMimeType(blob.type || "image/jpeg");
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setLoading(false);
      })
      .catch(() => {
        if (!revoked) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
    };
  }, [tab.artifact.url, tab.artifact.source, tab.artifact.mime_type]);

  // ── Clean up blob URL on unmount ──────────────────────────────
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // ── Reset pan when zoom drops to fit-to-width (≤ 100) ─────────
  useEffect(() => {
    if (zoom <= 100) {
      setPanX(0);
      setPanY(0);
    }
  }, [zoom]);

  // ── Zoom handlers ─────────────────────────────────────────────
  const handleZoomIn = useCallback(() => {
    const nextIdx = ZOOM_STEPS.findIndex((z) => z > zoom);
    if (nextIdx !== -1) {
      dispatch(setArtifactZoom(ZOOM_STEPS[nextIdx]));
    }
  }, [zoom, dispatch]);

  const handleZoomOut = useCallback(() => {
    const prevSteps = ZOOM_STEPS.filter((z) => z < zoom);
    if (prevSteps.length > 0) {
      dispatch(setArtifactZoom(prevSteps[prevSteps.length - 1]));
    }
  }, [zoom, dispatch]);

  const handleFit = useCallback(() => {
    dispatch(setArtifactZoom(100));
    setPanX(0);
    setPanY(0);
  }, [dispatch]);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const extMap: Record<string, string> = {
      "image/jpeg": ".jpg",
      "image/png": ".png",
      "image/gif": ".gif",
      "image/webp": ".webp",
    };
    const ext = extMap[mimeType] || ".jpg";
    const title = tab.artifact.title || "artifact-image";
    const hasExt = /\.\w{2,4}$/.test(title);
    const filename = hasExt ? title : `${title}${ext}`;

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [blobUrl, tab.artifact.title, mimeType]);

  // ── Keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === "-") {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === "ArrowLeft" && canPan) {
        e.preventDefault();
        setPanX((p) => p + PAN_STEP);
      } else if (e.key === "ArrowRight" && canPan) {
        e.preventDefault();
        setPanX((p) => p - PAN_STEP);
      } else if (e.key === "ArrowUp" && canPan) {
        e.preventDefault();
        setPanY((p) => p + PAN_STEP);
      } else if (e.key === "ArrowDown" && canPan) {
        e.preventDefault();
        setPanY((p) => p - PAN_STEP);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleZoomIn, handleZoomOut, canPan]);

  // ── Pan via mouse drag ────────────────────────────────────────
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!canPan) return;
      setIsDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY, panX, panY };
    },
    [canPan, panX, panY]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPanX(dragStart.current.panX + dx);
      setPanY(dragStart.current.panY + dy);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Cursor classes for the canvas
  const cursorClass = canPan
    ? isDragging
      ? "cursor-grabbing"
      : "cursor-grab"
    : "cursor-default";

  // Image inline transform (driven by pan state, kept inline for live updates).
  const imgStyle: React.CSSProperties = {
    maxWidth: zoom <= 100 ? "100%" : "none",
    width: zoom > 100 ? `${zoom}%` : "auto",
    maxHeight: zoom <= 100 ? "100%" : "none",
    transform: canPan ? `translate(${panX}px, ${panY}px)` : "none",
    transition: isDragging ? "none" : "transform 0.1s ease",
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Image area */}
      <div
        onMouseDown={handleMouseDown}
        className={[
          "relative flex-1 overflow-auto select-none text-center bg-midas-bone",
          cursorClass,
        ].join(" ")}
      >
        {loading && (
          <div
            className="m-4 h-[300px] w-[calc(100%-2rem)] animate-pulse rounded-md bg-midas-warm-stone/60"
            aria-hidden="true"
          />
        )}

        {error && !loading && (
          <div className="flex h-full w-full items-center justify-center font-sora text-[13px] text-midas-muted-stone">
            {translate("midas.artifact.loadError")}
          </div>
        )}

        {blobUrl && !loading && !error && (
          <img
            src={blobUrl}
            alt={tab.artifact.title}
            draggable={false}
            style={imgStyle}
          />
        )}
      </div>

      {/* Bottom toolbar — per Paper Phase 2 Dock Spec panel E ("DIAGRAM CANVAS")
          and Image Viewer artboard: 36–40px height, 28×28 hit targets, mono %
          readout with min-width 36px. Only diagram has step-nav; image keeps
          zoom out / % / zoom in / fit / download. */}
      <div className="flex h-[40px] flex-shrink-0 items-center justify-center gap-[6px] border-t border-midas-stone bg-midas-bone-light px-2">
        <button
          type="button"
          onClick={handleZoomOut}
          disabled={zoom <= ZOOM_STEPS[0]}
          aria-label={translate("midas.artifact.zoomOut")}
          title={translate("midas.artifact.zoomOut")}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-midas-warm-graphite transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <Minus size={16} strokeWidth={2} />
        </button>

        <span
          className="font-mono text-[11px] font-semibold text-midas-warm-graphite text-center"
          style={{ minWidth: "36px" }}
          aria-live="polite"
        >
          {zoom}%
        </span>

        <button
          type="button"
          onClick={handleZoomIn}
          disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
          aria-label={translate("midas.artifact.zoomIn")}
          title={translate("midas.artifact.zoomIn")}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-midas-warm-graphite transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <Plus size={16} strokeWidth={2} />
        </button>

        {/* Hairline separator */}
        <span
          aria-hidden="true"
          className="mx-1 inline-block h-4 w-px bg-midas-stone"
        />

        <button
          type="button"
          onClick={handleFit}
          aria-label={translate("midas.artifact.fit")}
          title={translate("midas.artifact.fit")}
          className="inline-flex h-7 items-center gap-[5px] rounded-md px-2 font-mono text-[11px] uppercase tracking-[0.06em] text-midas-warm-graphite transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite"
        >
          <Maximize2 size={14} strokeWidth={2} />
          <span className="hidden sm:inline">
            {translate("midas.artifact.fit")}
          </span>
        </button>

        <button
          type="button"
          onClick={handleDownload}
          disabled={!blobUrl}
          aria-label={translate("midas.artifact.download")}
          title={translate("midas.artifact.download")}
          className="inline-flex h-7 items-center gap-[5px] rounded-md px-2 font-mono text-[11px] uppercase tracking-[0.06em] text-midas-warm-graphite transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <Download size={14} strokeWidth={2} />
          <span className="hidden sm:inline">
            {translate("midas.artifact.download")}
          </span>
        </button>
      </div>
    </div>
  );
};

export default MidasArtifactImageViewerV2;
